import React, { useState, useEffect } from 'react';
import { Modal, StyleSheet, View as RNView } from 'react-native';
import { Surface, useTheme, Text, List, IconButton, Checkbox, Button } from 'react-native-paper';
import { ColumnDefinition } from '../../types/table';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import Cookies from 'js-cookie';

// Use div instead of View for web compatibility with react-beautiful-dnd
const View = RNView as any;

interface ColumnVisibilityModalProps {
    visible: boolean;
    onDismiss: () => void;
    columns: ColumnDefinition[];
    visibleColumns: string[];
    onToggleColumn: (columnKey: string) => void;
    onReorderColumns?: (columnKeys: string[]) => void;
    tableName: string;  // Used for unique cookie storage
}

const COOKIE_EXPIRY = 365; // Days
const getCookieKey = (tableName: string) => `table_${tableName}_columns`;

export const ColumnVisibilityModal: React.FC<ColumnVisibilityModalProps> = ({
    visible,
    onDismiss,
    columns,
    visibleColumns,
    onToggleColumn,
    onReorderColumns,
    tableName,
}) => {
    const theme = useTheme();
    // debugLog('ColumnVisibilityModal rendered', { visible, columnsCount: columns.length });

    // State to track the current order of columns
    const [orderedColumns, setOrderedColumns] = useState<ColumnDefinition[]>(columns || []);
    const [hasChanges, setHasChanges] = useState(false);

    // Update local state when columns prop changes, maintaining current order
    useEffect(() => {
        if (!orderedColumns.length) {
            // Only set initial order if we don't have one
            setOrderedColumns(columns || []);
        } else {
            // Maintain current order but update with any new columns
            const currentKeys = new Set(orderedColumns.map(col => col.key));
            const newColumns = columns.filter(col => !currentKeys.has(col.key));
            if (newColumns.length > 0) {
                setOrderedColumns([...orderedColumns, ...newColumns]);
            }
        }
        setHasChanges(false);
    }, [columns]);

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const items = Array.from(orderedColumns);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setOrderedColumns(items);
        setHasChanges(true);
        if (onReorderColumns) {
            onReorderColumns(items.map(col => col.key));
        }
    };

    const handleToggleColumn = (columnKey: string) => {
        setHasChanges(true);
        onToggleColumn(columnKey);
    };

    const handleSaveState = () => {
        const state = {
            orderedColumns: orderedColumns.map(col => col.key),
            visibleColumns
        };
        Cookies.set(getCookieKey(tableName), JSON.stringify(state), { expires: COOKIE_EXPIRY });
        setHasChanges(false);
    };

    const handleRestoreDefaults = () => {
        Cookies.remove(getCookieKey(tableName));
        setOrderedColumns(columns);
        // Reset visible columns to default (all columns visible)
        columns.forEach(col => {
            if (!visibleColumns.includes(col.key)) {
                onToggleColumn(col.key);
            }
        });
        if (onReorderColumns) {
            onReorderColumns(columns.map(col => col.key));
        }
        setHasChanges(false);
    };

    useEffect(() => {
        const cookieKey = getCookieKey(tableName);
        const storedState = Cookies.get(cookieKey);
        if (storedState) {
            const state = JSON.parse(storedState);
            setOrderedColumns(columns.filter(col => state.orderedColumns.includes(col.key)));
            state.visibleColumns.forEach((col: string) => {
                if (!visibleColumns.includes(col)) {
                    onToggleColumn(col);
                }
            });
        }
    }, [tableName, columns, visibleColumns, onToggleColumn]);

    return (
        <Modal
            visible={visible}
            onRequestClose={onDismiss}
            transparent={true}
            animationType="fade"
        >
            <View style={styles.modalOverlay}>
                <Surface style={[styles.modalContainer]} elevation={5}>
                    <Text variant="titleLarge" style={styles.title}>Customize Columns</Text>
                    <Text variant="bodyMedium" style={styles.subtitle}>
                        Toggle visibility and drag to reorder columns
                    </Text>

                    {orderedColumns.length > 0 ? (
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="droppable">
                                {(provided) => (
                                    <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        style={{
                                            maxHeight: 400,
                                            overflow: 'auto',
                                            marginTop: 8,
                                            marginBottom: 8,
                                            padding: 4,
                                            borderRadius: 4,
                                            backgroundColor: theme.colors.background
                                        }}
                                    >
                                        {orderedColumns.map((item, index) => (
                                            <Draggable
                                                key={item.key}
                                                draggableId={item.key}
                                                index={index}
                                            >
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        style={{
                                                            ...provided.draggableProps.style as any,
                                                            backgroundColor: snapshot.isDragging ? theme.colors.surfaceVariant : theme.colors.surface,
                                                            marginBottom: 4,
                                                            borderRadius: 4,
                                                            padding: '4px 8px'
                                                        }}
                                                    >
                                                        <View style={styles.leftContent}>
                                                            <View style={styles.controlsContainer}>
                                                                <Checkbox
                                                                    status={visibleColumns.includes(item.key) ? 'checked' : 'unchecked'}
                                                                    onPress={() => handleToggleColumn(item.key)}
                                                                />
                                                                <IconButton
                                                                    icon="drag"
                                                                    size={20}
                                                                />
                                                            </View>
                                                            <Text style={styles.columnTitle}>{item.title}</Text>
                                                        </View>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    ) : (
                        <Text>No columns available</Text>
                    )}

                    <View style={styles.actions}>
                        <Button
                            mode="outlined"
                            onPress={handleRestoreDefaults}
                            style={styles.actionButton}
                        >
                            Restore Defaults
                        </Button>
                        <Button
                            mode="contained"
                            onPress={handleSaveState}
                            style={styles.actionButton}
                            disabled={!hasChanges}
                        >
                            Save Layout
                        </Button>
                        <Button
                            mode="contained"
                            onPress={onDismiss}
                            style={styles.actionButton}
                        >
                            Done
                        </Button>
                    </View>
                </Surface>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    modalContainer: {
        width: '80%',
        maxWidth: 400,
        padding: 24,
        borderRadius: 8,
    },
    title: {
        marginBottom: 8,
        textAlign: 'center',
    },
    subtitle: {
        marginBottom: 16,
        opacity: 0.7,
        textAlign: 'center',
    },
    listContainer: {
        flex: 1,
        marginTop: 8,
        marginBottom: 8,
        borderRadius: 4,
    },
    leftContent: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
    },
    controlsContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginRight: 12,
        minWidth: 80,
    },
    columnTitle: {
        flex: 1,
    },
    actions: {
        marginTop: 16,
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 8,
    },
    actionButton: {
        marginHorizontal: 4,
    },
});
